import { useCallback, useEffect, useRef, useState } from 'react';
import { ACTIVE_STATUSES, JOBS_UPDATED_EVENT, POLL_INTERVAL_MS } from '@/constants';
import type { JobDetails, PromoJob } from '@/types';
import { fetchJobDetails, fetchJobs, haveJobsChanged, isJobWithinRecentDays, sortJobs } from '@/utils';
import { sortByStatus } from '@/utils/sortByStatus';
export const usePromoJobs = () => {
  const [promoJobs, setPromoJobs] = useState<PromoJob[]>([]);
  const [jobDetails, setJobDetails] = useState<JobDetails[]>([]);
  const [isJobsLoading, setIsJobsLoading] = useState<boolean>(false);
  const promoJobsRef = useRef<PromoJob[]>([]);
  const updateJobsIfChanged = (newJobs: PromoJob[]) => {
    const filteredJobs = newJobs.filter(job => isJobWithinRecentDays(job.createdAt));
    const sortedJobs = sortJobs(filteredJobs);
    if (haveJobsChanged(promoJobsRef.current, sortedJobs)) {
      promoJobsRef.current = sortedJobs;
      setPromoJobs(sortedJobs);
    }
  };
  const fetchAndUpdateJobs = useCallback(async (withLoading = true) => {
    try {
      if (withLoading) {
        setIsJobsLoading(true);
      }
      const [jobs, jobDetails_0] = await Promise.all([fetchJobs(), fetchJobDetails()]);
      updateJobsIfChanged(jobs);
      setJobDetails(sortByStatus(jobDetails_0));
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setIsJobsLoading(false);
    }
  }, []);
  const pollActiveJobs = useCallback(async () => {
    const hasActiveJobs = promoJobsRef.current.some(job_0 => ACTIVE_STATUSES.includes(job_0.status));
    if (hasActiveJobs) {
      await fetchAndUpdateJobs(false);
    }
  }, [fetchAndUpdateJobs]);
  useEffect(() => {
    fetchAndUpdateJobs();
    window.addEventListener(JOBS_UPDATED_EVENT, fetchAndUpdateJobs as unknown as EventListener);
    const interval = setInterval(async () => {
      try {
        await pollActiveJobs();
      } catch (error_0) {
        console.error('Polling error:', error_0);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      window.removeEventListener(JOBS_UPDATED_EVENT, fetchAndUpdateJobs as unknown as EventListener);
    };
  }, [fetchAndUpdateJobs, pollActiveJobs]);
  return {
    promoJobs,
    jobDetails,
    isJobsLoading
  };
};